import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import '../styles/Habit.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
const DEFAULT_NAMES = ["早起", "阅读", "健身", "喝水", "背单词", "冥想", "写作", "护肤", "代码", "早睡"];
const DEFAULT_ROW_COUNT = 20;

// Stable hue per column index, stepping 36° around the wheel (matches
// original 10-column palette; extras after col 9 just keep stepping).
const hueFor = (i) => (i * 36) % 360;

function authHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function LoginPrompt({ onLogin }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const submit = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/auth/login`, { password });
      localStorage.setItem('token', res.data.token);
      onLogin(res.data.token);
    } catch { setError('密码错误'); }
  };
  return (
    <div className="habit-login">
      <form onSubmit={submit}>
        <h2>Habit Tracker</h2>
        <input type="password" placeholder="密码" value={password}
          onChange={e => setPassword(e.target.value)} autoFocus />
        {error && <p className="habit-login-error">{error}</p>}
        <button type="submit" className="btn btn-primary">进入</button>
      </form>
    </div>
  );
}

function HabitGrid({ token }) {
  const [names, setNames] = useState(DEFAULT_NAMES);
  const [data, setData] = useState({});
  const [rowCount, setRowCount] = useState(DEFAULT_ROW_COUNT);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const saveTimer = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/habits`, { headers: authHeaders() })
      .then(res => {
        const d = res.data || {};
        setNames(Array.isArray(d.names) && d.names.length > 0 ? d.names : DEFAULT_NAMES);
        setData(d.data || {});
        setRowCount(d.rowCount || DEFAULT_ROW_COUNT);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [token]);

  const scheduleSave = useCallback((nextNames, nextData, nextRowCount) => {
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      axios.put(`${API}/habits`,
        { names: nextNames, data: nextData, rowCount: nextRowCount },
        { headers: authHeaders() }).catch(() => {});
    }, 600);
  }, []);

  const updateName = (i, v) => {
    const next = [...names];
    next[i] = v.trim() || `习惯${i + 1}`;
    setNames(next);
    scheduleSave(next, data, rowCount);
  };

  const updateValue = (key, v) => {
    const next = { ...data };
    if (v) next[key] = v; else delete next[key];
    setData(next);
    scheduleSave(names, next, rowCount);
  };

  const addRow = () => {
    // New day = highest row index, but the table renders newest-first, so
    // scroll to top (not bottom) to reveal it.
    const next = rowCount + 1;
    setRowCount(next);
    scheduleSave(names, data, next);
    setTimeout(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 80);
  };

  const addColumn = () => {
    const next = [...names, `习惯${names.length + 1}`];
    setNames(next);
    scheduleSave(next, data, rowCount);
  };

  const deleteColumn = (colIdx) => {
    if (names.length <= 1) {
      window.alert('至少保留一列');
      return;
    }
    if (!window.confirm(`删除"${names[colIdx]}"？这一列所有记录都会清掉。`)) return;
    const nextNames = names.filter((_, i) => i !== colIdx);
    const nextData = {};
    Object.entries(data).forEach(([k, v]) => {
      const m = k.match(/^(\d+)-(\d+)$/);
      if (m) {
        const r = parseInt(m[1], 10);
        const c = parseInt(m[2], 10);
        if (c === colIdx) return;                 // drop deleted col
        if (c > colIdx) nextData[`${r}-${c - 1}`] = v;  // shift higher cols down
        else nextData[k] = v;
      } else {
        nextData[k] = v;                          // date-N and other meta keys
      }
    });
    setNames(nextNames);
    setData(nextData);
    scheduleSave(nextNames, nextData, rowCount);
  };

  const reset = () => {
    if (!window.confirm('确认清空所有习惯数据？')) return;
    setNames(DEFAULT_NAMES);
    setData({});
    setRowCount(DEFAULT_ROW_COUNT);
    scheduleSave(DEFAULT_NAMES, {}, DEFAULT_ROW_COUNT);
  };

  const openCell = (r, c) => {
    setModal({ r, c, value: data[`${r}-${c}`] || '' });
  };

  const saveModal = () => {
    if (!modal) return;
    const key = `${modal.r}-${modal.c}`;
    updateValue(key, modal.value.trim());
    setModal(null);
  };

  if (loading) return <div className="habit-loading">加载中…</div>;

  // Row index r is a stable data key (date-${r}, ${r}-${c}) — never
  // reassigned. Only the DISPLAY order is newest-first: r=rowCount-1
  // (the most recently added day) renders at the top.
  const rows = [];
  for (let r = rowCount - 1; r >= 0; r--) rows.push(r);

  const gridStyle = {
    gridTemplateColumns: `26px 110px repeat(${names.length}, minmax(50px, 1fr))`,
  };

  return (
    <div className="habit-page">
      <header className="habit-page-header">
        <h1>Habit Tracker</h1>
      </header>
      <div className="habit-container">
        <div className="habit-scroll" ref={scrollRef}>
          <div className="habit-grid" style={gridStyle}>
            <div className="habit-col-header habit-col-num">#</div>
            <div className="habit-col-header habit-col-date">日期</div>
            {names.map((n, c) => (
              <div
                key={c}
                className="habit-col-header"
                style={{ '--col-h': hueFor(c) }}
              >
                <span
                  className="habit-col-name"
                  contentEditable
                  suppressContentEditableWarning
                  onBlur={(e) => updateName(c, e.target.textContent)}
                >
                  {n}
                </span>
                <button
                  className="habit-col-del"
                  onClick={(e) => { e.stopPropagation(); deleteColumn(c); }}
                  title="删除此列"
                  contentEditable={false}
                >×</button>
              </div>
            ))}

            {rows.map(r => (
              <React.Fragment key={r}>
                <div className="habit-row-num">{r + 1}</div>
                <input
                  type="date"
                  className="habit-date-input"
                  defaultValue={data[`date-${r}`] || ''}
                  onFocus={(e) => {
                    if (!e.target.value) {
                      const today = new Date().toISOString().slice(0, 10);
                      e.target.value = today;
                      updateValue(`date-${r}`, today);
                    }
                  }}
                  onBlur={(e) => updateValue(`date-${r}`, e.target.value)}
                />
                {names.map((_, c) => {
                  const v = data[`${r}-${c}`];
                  return (
                    <div
                      key={c}
                      className={`habit-cell${v ? ' active' : ''}`}
                      style={{ '--col-h': hueFor(c) }}
                      onClick={() => openCell(r, c)}
                      title={v || ''}
                    >
                      {v || ''}
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </div>
        <div className="habit-footer">
          <button className="btn btn-primary" onClick={addRow}>+ 新的一天</button>
          <button className="btn btn-secondary btn-small" onClick={addColumn}>+ 加列</button>
          <button className="habit-reset" onClick={reset}>重置</button>
        </div>
      </div>

      {modal && (
        <div className="habit-modal-overlay" onClick={() => setModal(null)}>
          <div className="habit-modal" onClick={e => e.stopPropagation()}>
            <h3>{names[modal.c]}</h3>
            <input
              type="text"
              maxLength={30}
              autoFocus
              value={modal.value}
              onChange={e => setModal({ ...modal, value: e.target.value })}
              onKeyDown={e => { if (e.key === 'Enter') saveModal(); }}
              placeholder="点下方按钮或直接输入"
            />
            <div className="habit-quick-picks">
              {['✓','✗','⭐','🥛','🍅','🍊','30','60'].map(sym => (
                <button
                  key={sym}
                  type="button"
                  className="habit-pick"
                  onClick={() => {
                    const next = (modal.value + sym).slice(0, 30);
                    setModal({ ...modal, value: next });
                  }}
                >{sym}</button>
              ))}
              <button
                type="button"
                className="habit-pick habit-pick-clear"
                onClick={() => setModal({ ...modal, value: '' })}
                title="清空"
              >⌫</button>
            </div>
            <div className="habit-modal-actions">
              <button className="btn btn-small" onClick={() => setModal(null)}>取消</button>
              <button className="btn btn-primary btn-small" onClick={saveModal}>保存</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Habit() {
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  if (!token) return <LoginPrompt onLogin={setToken} />;
  return <HabitGrid token={token} />;
}
